// src/features/pdf/components/FileDropzone.tsx
import React, { useMemo } from 'react';
import { useDropzone, Accept } from 'react-dropzone';
import { Box, Typography, useTheme } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';

interface FileDropzoneProps {
    onDrop: (acceptedFiles: File[]) => void;
    accept: Accept;
    multiple?: boolean;
    disabled?: boolean;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({ onDrop, accept, multiple = true, disabled = false }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        accept,
        multiple,
        disabled,
    });

    const acceptedFormats = useMemo(() =>
            Object.values(accept).flat().join(', ').toUpperCase(),
        [accept]
    );

    const baseStyle: React.CSSProperties = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        borderWidth: 2,
        borderRadius: theme.shape.borderRadius,
        borderColor: theme.palette.divider,
        borderStyle: 'dashed',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.secondary,
        outline: 'none',
        transition: 'border .24s ease-in-out, background-color .24s ease-in-out',
        cursor: 'pointer',
    };

    const activeStyle: React.CSSProperties = {
        borderColor: theme.palette.primary.main,
    };

    const acceptStyle: React.CSSProperties = {
        borderColor: theme.palette.success.main,
        backgroundColor: theme.palette.action.hover,
    };

    const rejectStyle: React.CSSProperties = {
        borderColor: theme.palette.error.main,
        backgroundColor: theme.palette.action.hover,
    };

    const disabledStyle: React.CSSProperties = {
        cursor: 'not-allowed',
        opacity: 0.5,
    };

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
        ...(disabled ? disabledStyle : {}),
    }), [isDragActive, isDragReject, isDragAccept, disabled, theme]);

    return (
        <Box {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <UploadFileIcon sx={{ fontSize: 48, mb: 2, color: theme.palette.primary.main }} />
            <Typography variant="h6" component="p">
                {t('dropzone_drag_or_click', { ns: 'tools' })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
                {t('dropzone_supported_formats', { formats: acceptedFormats, ns: 'tools' })}
            </Typography>
        </Box>
    );
};

export default FileDropzone;